import { useState } from 'react';
import { motion } from 'framer-motion';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Search, FileText, Plus, DollarSign, Clock, Users } from 'lucide-react';
import { demoContracts } from '../data/dummyData';
import { AinaaBadge } from '../components/AinaaBadge';
import Breadcrumbs from '../components/Breadcrumbs';

const fadeUp = { hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0 } };

const statusColor = {
  signed: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
  draft: 'bg-secondary text-muted-foreground',
  sent: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
};

export default function ContractsPage() {
  const [search, setSearch] = useState('');

  const filtered = demoContracts.filter(c =>
    !search || c.title.toLowerCase().includes(search.toLowerCase()) || c.description.toLowerCase().includes(search.toLowerCase()) || (c.ainaa_id && c.ainaa_id.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <motion.div initial="hidden" animate="visible" variants={{ visible: { transition: { staggerChildren: 0.05 } } }} data-testid="contracts-page">
      <Breadcrumbs />
      <motion.div variants={fadeUp} className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-medium tracking-tight">Contracts</h1>
          <p className="text-sm text-muted-foreground mt-1">{demoContracts.length} contracts tracked.</p>
        </div>
        <Button size="sm" className="gap-2 h-9" data-testid="new-contract-btn"><Plus className="w-4 h-4" strokeWidth={2} /> New Contract</Button>
      </motion.div>

      <motion.div variants={fadeUp} className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input placeholder="Search contracts..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 h-9" data-testid="search-contracts-input" />
        </div>
      </motion.div>

      <motion.div variants={fadeUp} className="space-y-2">
        {filtered.map(c => (
          <div key={c.contract_id} className="p-5 rounded-xl border border-border/50 bg-card hover:border-border transition-all" data-testid={`contract-${c.contract_id}`}>
            <div className="flex items-start justify-between mb-2">
              <div className="min-w-0 flex-1">
                <h3 className="text-sm font-medium">{c.title}</h3>
                <p className="text-xs text-muted-foreground mt-1">{c.description}</p>
              </div>
              <Badge className={`text-[10px] h-5 px-1.5 capitalize flex-shrink-0 ml-3 ${statusColor[c.status] || ''}`}>{c.status}</Badge>
            </div>
            {c.ainaa_id && <div className="mt-1.5"><AinaaBadge ainaaId={c.ainaa_id} /></div>}
            <div className="flex flex-wrap items-center gap-4 mt-3 text-[10px] text-muted-foreground">
              <span className="flex items-center gap-1"><DollarSign className="w-3 h-3" strokeWidth={2} />{c.value ? `₹${(c.value/1000).toFixed(0)}K` : '—'}</span>
              <span className="flex items-center gap-1"><Clock className="w-3 h-3" strokeWidth={2} />{c.start_date} — {c.end_date}</span>
              <span className="flex items-center gap-1"><Users className="w-3 h-3" strokeWidth={2} />{c.parties.join(', ')}</span>
            </div>
          </div>
        ))}
      </motion.div>

      {filtered.length === 0 && (
        <div className="text-center py-16">
          <FileText className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" strokeWidth={2} />
          <p className="text-sm text-muted-foreground">No contracts found</p>
        </div>
      )}
    </motion.div>
  );
}
